import { createTheme } from "@mui/material/styles"

const COLLECTION_TYPOGRAPHY = "Typography"
const COLLECTION_EFFECTS = "Effects"
const COLLECTION_SPACING = "Spacing"

const MuiColorsVariants = {
    main: "main",
    light: "light",
    dark: "dark",
    contrast: "contrastText",
}

const INNER_SHADOW_TYPE = "INNER_SHADOW"

const FontWeightMapping = {
    "Bold": 700,
    "SemiBold": 600,
    "Medium": 500,
    "Regular": 400,
    "Light": 300,
}

const TextCaseMapping = {
    "ORIGINAL": "none",
    "UPPER": "uppercase",
    "LOWER": "lowercase",
    "TITLE": "capitalize",
}

function getCollection(variablesJson, name, modeName = null) {
    const collection = variablesJson.collections?.find(c => c.name === name)
    if (!collection) {
        return []
    }

    if (modeName) {
        const mode = collection.modes?.find(m => m.name === modeName)
        return mode?.variables || []
    }

    return collection.modes?.[0]?.variables || []
}

function resolveValue(variablesJson, variable) {
    if (!variable.isAlias) {
        return variable.value
    }

    const refCollection = variable.value?.collection
    const refName = variable.value?.name

    if (!refCollection || !refName) {
        return null
    }

    const collection = getCollection(variablesJson, refCollection)
    const referencedVar = collection.find(v => v.name === refName)

    if (!referencedVar) {
        return null
    }

    return resolveValue(variablesJson, referencedVar)
}

function formatBoxShadows(variablesJson) {
    const effectsCollection = getCollection(variablesJson, COLLECTION_EFFECTS)

    return effectsCollection.reduce((acc, effect) => {
        if (effect.type !== "effect" || !effect.value?.effects?.[0]) {
            return acc
        }

        const effectValue = effect.value.effects[0]
        const { r, g, b, a } = effectValue.color
        const colorString = `rgba(${Math.round(r * 255)}, ${Math.round(g * 255)}, ${Math.round(b * 255)}, ${a})`
        const shadowType = effectValue.type === INNER_SHADOW_TYPE ? " inset" : ""
        const boxShadow = `${effectValue.offset.x}px ${effectValue.offset.y}px ${effectValue.radius}px ${effectValue.spread}px ${colorString}${shadowType}`

        acc.shadowsKeys.push(effect.name)
        acc.shadows.push(boxShadow)
        return acc
    }, { shadows: [], shadowsKeys: [] })
}

function selectShadow(shadowKey = 0) {
    const index = typeof shadowKey === "number" ? shadowKey : this.shadowsKeys.indexOf(shadowKey) + 25
    return this.shadows[index]
}

function formatTypography(variablesJson) {
    const typographyCollection = getCollection(variablesJson, COLLECTION_TYPOGRAPHY)

    return typographyCollection.reduce((acc, typography) => {
        if (typography.type !== "typography" || !typography.value) {
            return acc
        }

        const typographyValue = { ...typography.value }

        if (typographyValue.fontWeight && FontWeightMapping[typographyValue.fontWeight]) {
            typographyValue.fontWeight = FontWeightMapping[typographyValue.fontWeight]
        }

        if (typographyValue.textCase && TextCaseMapping[typographyValue.textCase]) {
            typographyValue.textTransform = TextCaseMapping[typographyValue.textCase]
            delete typographyValue.textCase
        }

        delete typographyValue.lineHeightUnit
        delete typographyValue.letterSpacingUnit
        delete typographyValue.textDecoration

        return {
            ...acc,
            [typography.name]: typographyValue,
        }
    }, {})
}

function formatSpacing(variablesJson) {
    const spacingCollection = getCollection(variablesJson, COLLECTION_SPACING)

    const spacings = spacingCollection.reduce((acc, spacing) => {
        if (spacing.type !== "number") {
            return acc
        }

        return {
            ...acc,
            [spacing.name]: spacing.value,
        }
    }, {})

    return (factor = 1) => {
        if (factor === "auto") {
            return "auto"
        }
        if (typeof factor === "string" && spacings[factor] !== undefined) {
            return spacings[factor]
        }
        if (typeof factor === "number") {
            return factor * 4
        }
        return factor
    }
}

function buildMuiPalette(variablesJson, collectionName, modeName = "Light") {
    const tokensCollection = getCollection(variablesJson, collectionName, modeName)
    const palette = {}

    tokensCollection.forEach(variable => {
        if (variable.type !== "color") {
            return
        }

        const resolvedValue = resolveValue(variablesJson, variable)
        if (!resolvedValue) {
            return
        }

        const parts = variable.name.split("/")
        const lastPart = parts[parts.length - 1]

        const isLastPartAVariant = Object.keys(MuiColorsVariants).includes(lastPart)
        const colorVariant = isLastPartAVariant ? MuiColorsVariants[lastPart] : "main"
        const colorName = isLastPartAVariant ? parts.slice(0, -1).join("/") : variable.name

        if (!palette[colorName]) {
            palette[colorName] = {
                [colorVariant]: resolvedValue,
            }
        } else {
            palette[colorName][colorVariant] = resolvedValue
        }
    })

    const baseTheme = createTheme()

    const augmentedPalette = Object.fromEntries(
        Object.entries(palette).map(([key, value]) => [
            key,
            baseTheme.palette.augmentColor({ color: value, name: key }),
        ]),
    )

    return augmentedPalette
}

export function createThemeFromJson(variablesJson, options = {}) {
    const {
        tokenCollection = "Logiroad Center",
        mode = "Light",
    } = options

    const muiPaletteBase = buildMuiPalette(variablesJson, "Colors (base)", "Mode 1")
    const muiPalette = buildMuiPalette(variablesJson, tokenCollection, mode)
    const typography = formatTypography(variablesJson)
    const boxShadows = formatBoxShadows(variablesJson)
    const spacing = formatSpacing(variablesJson)

    const firstTypography = Object.values(typography)[0]
    const fontFamily = firstTypography?.fontFamily || "Poppins"

    const palette = {
        map: {
            primary: "#317EDE",
            disabled: "#9D9FB1",
            darkReferential: "#333333",
            lightReferential: "#FFFFFF",
            text: "#FFFFFF",
        },
        ...muiPaletteBase,
        ...muiPalette,
    }

    const theme = createTheme({
        palette: {
            ...palette,
            divider: palette["divider"]?.main,
        },
        typography: {
            fontFamily,
            ...typography,
        },
        spacing: spacing,
        components: {
            icons: {
                defaultHeight: 24,
                defaultWidth: 24,
            },
            chip: {
                defaultHeight: 32,
            },
            MuiFormHelperText: {
                styleOverrides: {
                    root: {
                        marginTop: 0,
                        height: 0,
                    },
                },
            },
            MuiButton: {
                styleOverrides: {
                    root: {
                        textTransform: "none",
                    },
                },
            },
            MuiLink: {
                styleOverrides: {
                    root: {
                        fontFamily: "Poppins",
                    },
                },
            },
        },
    })

    const newTheme = {
        ...theme,
        palette: {
            ...theme.palette,
        },
        shadows: [
            ...theme.shadows,
            ...boxShadows.shadows,
        ],
        shadowsKeys: boxShadows.shadowsKeys || [],
    }

    newTheme.customShadow = selectShadow.bind(newTheme)

    return newTheme
}